/**
 * Offline Existing-Asset Queue (v1.1.25)
 * ======================================
 * Separate IndexedDB store for queueing "add photos + re-analyze" operations
 * targeting an EXISTING analysis_id (typically a spreadsheet-preloaded asset).
 *
 * DESIGN INVARIANT (per product owner directive 2026-02-06):
 *   This module is COMPLETELY INDEPENDENT from the existing offline queue
 *   (services/offlineStorage.js). It uses a different database name,
 *   different store names, and a different sync worker. If this entire
 *   file is deleted, the existing offline flow for NEW assets continues
 *   to work byte-for-byte identically.
 *
 * Storage Structure:
 *   - Database: fmiq_offline_existing_asset_db   (note: DIFFERENT db from offlineStorage.js)
 *   - Store:    pending_existing_asset_ops       (record shape below)
 *
 * Record shape:
 *   {
 *     id: <autoincrement>,
 *     op_type: 'add_images_then_reanalyze',
 *     analysis_id: '<existing analysis UUID>',
 *     assessment_id: '<for diagnostic grouping>',
 *     photos_base64: [<data URI>, ...],
 *     queued_at: <ISO timestamp>,
 *     status: 'pending' | 'syncing' | 'completed' | 'failed',
 *     retry_count: <int>,
 *     last_error: <string|null>,
 *   }
 *
 * This file: queue/dequeue/status update primitives only.
 * The sync worker that drains the queue lives in
 * services/offlineExistingAssetSync.js.
 */

const DB_NAME = 'fmiq_offline_existing_asset_db';
const DB_VERSION = 1;
const OPS_STORE = 'pending_existing_asset_ops';

let dbInstance = null;
let dbFailed = false;

/**
 * Initialize IndexedDB database. Defensive — returns rejected promise on
 * failure rather than crashing. Caller is expected to fall back to
 * "online-only" behaviour if init fails.
 */
export const initExistingAssetDB = () => {
  return new Promise((resolve, reject) => {
    if (dbFailed) {
      reject(new Error('Existing-asset offline DB previously failed; not retrying this session'));
      return;
    }
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    try {
      if (!window.indexedDB) {
        dbFailed = true;
        reject(new Error('IndexedDB not available'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      // 5s safety timeout — same as existing offlineStorage convention.
      const timeout = setTimeout(() => {
        dbFailed = true;
        reject(new Error('Existing-asset offline DB open timed out'));
      }, 5000);

      request.onerror = () => {
        clearTimeout(timeout);
        dbFailed = true;
        reject(request.error);
      };

      request.onblocked = () => {
        clearTimeout(timeout);
        dbFailed = true;
        reject(new Error('Existing-asset offline DB blocked by another connection'));
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        dbInstance = request.result;
        dbInstance.onclose = () => {
          dbInstance = null;
        };
        dbInstance.onversionchange = () => {
          dbInstance.close();
          dbInstance = null;
        };
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(OPS_STORE)) {
          const store = db.createObjectStore(OPS_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('analysis_id', 'analysis_id', { unique: false });
          store.createIndex('assessment_id', 'assessment_id', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }
      };
    } catch (err) {
      dbFailed = true;
      reject(err);
    }
  });
};

/**
 * Enqueue an "add photos + re-analyze" op for an existing asset.
 * Returns the assigned record id on success.
 *
 * v1.1.26 — calls the EXISTING storageManager.checkCanStore() before
 * enqueuing so this queue doesn't starve the browser quota that the
 * existing fresh-asset queue also depends on. Throws a clear error
 * (status: 'critical') if storage is full so the caller can show a
 * helpful toast and the assessor knows to sync before capturing more.
 */
export const enqueueAddImagesThenReanalyze = async (
  analysisId,
  assessmentId,
  photosBase64
) => {
  return _enqueueOp({
    op_type: 'add_images_then_reanalyze',
    analysis_id: analysisId,
    assessment_id: assessmentId,
    photos_base64: photosBase64,
  });
};

/**
 * v1.1.53 — Enqueue an "add photos only" op (no automatic reanalyze).
 * Used by the simple "Add to Asset" button on the existing-asset card
 * so the on-site team's photos still queue offline even when they don't
 * want the AI re-analysis to trigger automatically. The sync worker
 * branches on op_type and skips Stage 2 (reanalyze) for these entries.
 */
export const enqueueAddPhotosOnly = async (
  analysisId,
  assessmentId,
  photosBase64
) => {
  return _enqueueOp({
    op_type: 'add_photos_only',
    analysis_id: analysisId,
    assessment_id: assessmentId,
    photos_base64: photosBase64,
  });
};

/**
 * Internal helper — shared enqueue path for both op_types so the quota
 * guard and record bookkeeping stays in one place.
 */
const _enqueueOp = async ({ op_type, analysis_id, assessment_id, photos_base64 }) => {
  if (!analysis_id) {
    throw new Error('analysis_id is required');
  }
  if (!Array.isArray(photos_base64) || photos_base64.length === 0) {
    throw new Error('At least one photo is required');
  }

  // v1.1.26 quota guard. Lazy-import keeps this queue file standalone for
  // testing (the static-analysis tripwire validates module imports), and
  // a checkCanStore failure must not crash the enqueue path — we treat
  // an unknown-quota result as "allow."
  try {
    const { checkCanStore } = await import('../lib/storageManager');
    const result = await checkCanStore();
    if (result && result.canStore === false) {
      const err = new Error(result.message || 'Browser storage is nearly full');
      err.code = 'STORAGE_FULL';
      throw err;
    }
  } catch (storageErr) {
    if (storageErr?.code === 'STORAGE_FULL') {
      throw storageErr;
    }
    // checkCanStore unavailable or failed to compute — proceed (same behaviour
    // as the existing fresh-asset queue on quota-unknown).
  }

  const db = await initExistingAssetDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const record = {
      op_type,
      analysis_id,
      assessment_id: assessment_id || null,
      photos_base64,
      queued_at: new Date().toISOString(),
      status: 'pending',
      retry_count: 0,
      last_error: null,
    };
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

/**
 * Get all pending or failed ops (those still requiring sync).
 */
export const getPendingExistingAssetOps = async () => {
  const db = await initExistingAssetDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readonly');
    const store = tx.objectStore(OPS_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const all = req.result || [];
      resolve(all.filter(r => r.status === 'pending' || r.status === 'failed'));
    };
    req.onerror = () => reject(req.error);
  });
};

/**
 * Count of pending/failed ops — for badge display.
 */
export const getPendingExistingAssetCount = async () => {
  try {
    const ops = await getPendingExistingAssetOps();
    return ops.length;
  } catch {
    return 0;
  }
};

/**
 * Update an op's status, with optional error message bookkeeping.
 */
export const updateOpStatus = async (id, status, errorMessage = null) => {
  const db = await initExistingAssetDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (!record) {
        reject(new Error(`Op ${id} not found`));
        return;
      }
      record.status = status;
      record.last_updated = new Date().toISOString();
      if (errorMessage) {
        record.last_error = errorMessage;
        record.retry_count = (record.retry_count || 0) + 1;
      }
      if (status === 'completed') {
        record.completed_at = new Date().toISOString();
      }
      const putReq = store.put(record);
      putReq.onsuccess = () => resolve(record);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

/**
 * Remove completed ops from the queue (called periodically by the sync worker
 * to keep IndexedDB size bounded).
 */
export const clearCompletedOps = async () => {
  const db = await initExistingAssetDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    const store = tx.objectStore(OPS_STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const completed = (req.result || []).filter(r => r.status === 'completed');
      let deleted = 0;
      completed.forEach(record => {
        store.delete(record.id);
        deleted++;
      });
      resolve(deleted);
    };
    req.onerror = () => reject(req.error);
  });
};

/**
 * Debug/recovery: clear all queued ops.
 */
export const clearAllExistingAssetOps = async () => {
  const db = await initExistingAssetDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([OPS_STORE], 'readwrite');
    tx.objectStore(OPS_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};
